// src/pages/common-components/SellYourCar/SellYourCar.tsx
import React, { useState, useRef, useEffect } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import { submitTradeIn } from '../../Services/tradeInService';
import { useAuth } from '../../Contexts/AuthContext';

const CONDITIONS = ['Excellent', 'Good', 'Fair', 'Needs Repair'];

const MAX_IMAGES = 6;

const STEPS = [
  { title: 'Tell Us About Your Car', desc: 'Share your car\'s details and a few photos — takes about two minutes.' },
  { title: 'Get a Free Evaluation', desc: 'Our team reviews your submission and gets back to you with an offer.' },
  { title: 'Get Paid', desc: 'Happy with the offer? We handle the paperwork and pay you promptly.' },
];

const BENEFITS = [
  { icon: 'zmdi-money', title: 'Free, Fair Evaluation', desc: 'Get a no-obligation market valuation from our specialists.' },
  { icon: 'zmdi-check-circle', title: 'No Obligation', desc: 'Submit your details risk-free — you decide if you want to proceed.' },
  { icon: 'zmdi-time-restore', title: 'Fast Turnaround', desc: 'Most sellers hear back with an offer within one business day.' },
  { icon: 'zmdi-shield-check', title: 'Trusted Process', desc: 'We handle the paperwork so you get paid promptly and securely.' },
];

const SellYourCar: React.FC = () => {
  const { currentUser, customerProfile } = useAuth();
  const [form, setForm] = useState({
    ownerName: '', ownerPhone: '', ownerEmail: '', carMake: '', carModel: '',
    carYear: '', mileageKm: '', condition: 'Good', registrationNumber: '',
    askingPrice: '', description: '',
  });
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Object URLs are only created for newly-added files and revoked when their
  // file is removed or the component unmounts — regenerating every preview on
  // each add/remove would leak a blob URL for every prior render.
  useEffect(() => {
    return () => imagePreviews.forEach(url => URL.revokeObjectURL(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill from the signed-in customer's profile, if available.
  useEffect(() => {
    if (!currentUser) return;
    setForm((prev) => ({
      ...prev,
      ownerName: prev.ownerName || customerProfile?.fullName || currentUser.displayName || '',
      ownerEmail: prev.ownerEmail || customerProfile?.email || currentUser.email || '',
      ownerPhone: prev.ownerPhone || customerProfile?.phone || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, customerProfile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImagesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const room = MAX_IMAGES - images.length;
    const accepted = files.slice(0, room);
    setImages(prev => [...prev, ...accepted]);
    setImagePreviews(prev => [...prev, ...accepted.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    URL.revokeObjectURL(imagePreviews[index]);
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.ownerName.trim() || !form.ownerPhone.trim() || !form.carMake.trim() || !form.carModel.trim() || !form.carYear) {
      setError('Please fill in your name, phone number, and car make/model/year.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const id = await submitTradeIn({
        ownerName: form.ownerName, ownerPhone: form.ownerPhone, ownerEmail: form.ownerEmail || undefined,
        carMake: form.carMake, carModel: form.carModel, carYear: parseInt(form.carYear) || 0,
        mileageKm: form.mileageKm ? parseInt(form.mileageKm) : undefined, condition: form.condition,
        registrationNumber: form.registrationNumber || undefined,
        askingPrice: form.askingPrice ? parseFloat(form.askingPrice) : undefined,
        description: form.description || undefined,
        submittedByUid: currentUser?.uid,
      }, images);
      setReferenceId(id);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong submitting your car. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnother = () => {
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    setForm({
      ownerName: '', ownerPhone: '', ownerEmail: '', carMake: '', carModel: '',
      carYear: '', mileageKm: '', condition: 'Good', registrationNumber: '',
      askingPrice: '', description: '',
    });
    setImages([]);
    setImagePreviews([]);
    setReferenceId('');
    setSubmitted(false);
  };

  return (
    <div>
      <Header />

      <section
        className="bg-img1 txt-center p-lr-15 p-tb-92"
        style={{ backgroundImage: `url('asset/images/bg-10.png')` }}
      >
      </section>

      <section className="bg0 p-t-60 p-b-100">
        <div className="container">
          {submitted ? (
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div style={{ border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '32px 24px', background: '#f6f9f6', textAlign: 'center', borderBottom: '1px solid #e5e5e5' }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%', background: '#2e7d32', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28,
                      margin: '0 auto 14px',
                    }}>
                      &#10003;
                    </div>
                    <h4 style={{ marginBottom: 8 }}>Thank you, {form.ownerName}!</h4>
                    <p style={{ margin: 0, color: '#666' }}>
                      We've received your submission. Our team will review it and contact you shortly at {form.ownerPhone}
                      {form.ownerEmail ? ` or ${form.ownerEmail}` : ''}.
                    </p>
                    {referenceId && (
                      <p style={{ marginTop: 10, fontSize: 13, color: '#999' }}>
                        Reference ID: <span style={{ fontFamily: 'monospace' }}>{referenceId.slice(0, 8).toUpperCase()}</span>
                      </p>
                    )}
                  </div>

                  <div style={{ padding: '24px' }}>
                    <h5 style={{ marginBottom: 14, fontSize: 15 }}>Submission Summary</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', fontSize: 14 }}>
                      <SummaryRow label="Car" value={`${form.carMake} ${form.carModel} (${form.carYear})`} />
                      <SummaryRow label="Condition" value={form.condition} />
                      {form.mileageKm && <SummaryRow label="Mileage" value={`${form.mileageKm} km`} />}
                      {form.registrationNumber && <SummaryRow label="Registration No." value={form.registrationNumber} />}
                      {form.askingPrice && <SummaryRow label="Asking Price" value={form.askingPrice} />}
                      <SummaryRow label="Phone" value={form.ownerPhone} />
                      {form.ownerEmail && <SummaryRow label="Email" value={form.ownerEmail} />}
                    </div>
                    {form.description && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 13, color: '#999', marginBottom: 4 }}>Additional Details</div>
                        <p style={{ margin: 0, fontSize: 14, color: '#444' }}>{form.description}</p>
                      </div>
                    )}

                    {imagePreviews.length > 0 && (
                      <div style={{ marginTop: 18 }}>
                        <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>Photos ({imagePreviews.length})</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                          {imagePreviews.map((src) => (
                            <img
                              key={src}
                              src={src}
                              alt="Submitted car"
                              style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: 24, textAlign: 'center' }}>
                      <button type="button" onClick={handleSubmitAnother} className="syc-submit-btn" style={{ ...submitStyle, background: '#fff', color: '#222', border: '1px solid #ddd' }}>
                        Submit Another Car
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="syc-layout">
              {/* ── Form panel ── */}
              <div className="syc-form-card">
                <h4 className="mtext-105 cl2 p-b-30">Tell Us About Your Car</h4>

                <form onSubmit={handleSubmit} className="syc-form-grid">
                  {error && <div style={{ gridColumn: '1 / -1', color: '#c0392b', fontSize: 14 }}>{error}</div>}

                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Your Name *</label>
                    <input name="ownerName" value={form.ownerName} onChange={handleChange} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Phone Number *</label>
                    <input name="ownerPhone" value={form.ownerPhone} onChange={handleChange} style={inputStyle} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Email (optional)</label>
                    <input type="email" name="ownerEmail" value={form.ownerEmail} onChange={handleChange} style={inputStyle} />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Car Make *</label>
                    <input name="carMake" value={form.carMake} onChange={handleChange} placeholder="e.g. Toyota" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Car Model *</label>
                    <input name="carModel" value={form.carModel} onChange={handleChange} placeholder="e.g. Aqua" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Year *</label>
                    <input type="number" name="carYear" value={form.carYear} onChange={handleChange} placeholder="2018" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Mileage (km)</label>
                    <input type="number" name="mileageKm" value={form.mileageKm} onChange={handleChange} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Condition</label>
                    <select name="condition" value={form.condition} onChange={handleChange} style={selectStyle}>
                      {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Registration Number</label>
                    <input name="registrationNumber" value={form.registrationNumber} onChange={handleChange} style={inputStyle} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Your Asking Price (optional)</label>
                    <input type="number" name="askingPrice" value={form.askingPrice} onChange={handleChange} style={inputStyle} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>
                      Car Images ({images.length}/{MAX_IMAGES})
                    </label>

                    <div
                      onClick={() => images.length < MAX_IMAGES && fileInputRef.current?.click()}
                      className="syc-dropzone"
                      style={{
                        opacity: images.length >= MAX_IMAGES ? 0.5 : 1,
                        cursor: images.length >= MAX_IMAGES ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <i className="zmdi zmdi-camera" style={{ fontSize: 22, color: '#999' }}></i>
                      <span>Click to upload photos</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImagesSelected}
                        disabled={images.length >= MAX_IMAGES}
                        hidden
                      />
                    </div>

                    <p style={{ fontSize: 12, color: '#999', marginTop: 5 }}>
                      Add a few clear photos of your car (exterior, interior, mileage) — up to {MAX_IMAGES} images.
                    </p>
                    {imagePreviews.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
                        {imagePreviews.map((src, i) => (
                          <div key={src} style={{ position: 'relative', width: 84, height: 84 }}>
                            <img
                              src={src}
                              alt={`Car photo ${i + 1}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              aria-label="Remove image"
                              style={{
                                position: 'absolute', top: -8, right: -8, width: 22, height: 22,
                                borderRadius: '50%', border: 'none', background: '#222', color: '#fff',
                                fontSize: 13, lineHeight: 1, cursor: 'pointer',
                              }}
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Additional Details</label>
                    <textarea rows={4} name="description" value={form.description} onChange={handleChange} placeholder="Service history, modifications, condition notes..." style={inputStyle} />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <button type="submit" disabled={submitting} className="syc-submit-btn" style={submitStyle}>
                      {submitting ? 'Submitting...' : 'Submit for Evaluation'}
                    </button>
                  </div>
                </form>
              </div>

              {/* ── Info panel: why sell with us + how it works ── */}
              <div className="syc-info-card">
                <h4 className="mtext-105 cl2 p-b-14">Why Sell With Us</h4>
                <p className="stext-113 cl6 p-b-30">
                  Tell us about your car and our team will get back to you with a free evaluation and offer —
                  no obligation, no fees.
                </p>

                <div className="syc-benefits">
                  {BENEFITS.map((b) => (
                    <div key={b.title} className="syc-benefit-row">
                      <span className="syc-benefit-icon"><i className={`zmdi ${b.icon}`}></i></span>
                      <div>
                        <h5>{b.title}</h5>
                        <p>{b.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="syc-steps">
                  <h6 className="syc-steps-title">How It Works</h6>
                  {STEPS.map((step, i) => (
                    <div key={step.title} className="syc-step-row">
                      <div className="syc-step-num">{i + 1}</div>
                      <div>
                        <h6>{step.title}</h6>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        .syc-dropzone {
          display: flex; align-items: center; gap: 10px;
          padding: 16px; border: 1px dashed #ddd; border-radius: 4px;
          background: #f7f7f7; transition: all 0.2s;
        }
        .syc-dropzone:hover { background: #f0f0f0; border-color: #717fe0; }
        .syc-dropzone span { font-size: 14px; color: #666; }

        /* ── Two-column layout: info panel + form panel ── */
        .syc-layout {
          display: grid;
          grid-template-columns: 1.15fr 0.85fr;
          gap: 32px;
          align-items: start;
        }
        .syc-info-card, .syc-form-card {
          border: 1px solid #e6e6e6;
          border-radius: 12px;
          padding: 44px;
        }
        .syc-info-card { background: #f8f9ff; }
        .syc-form-card { background: #fff; }

        .syc-benefit-row { display: flex; gap: 14px; margin-bottom: 22px; }
        .syc-benefit-icon {
          width: 44px; height: 44px; border-radius: 50%; background: #eef0ff; color: #717fe0;
          display: flex; align-items: center; justify-content: center; font-size: 19px; flex-shrink: 0;
        }
        .syc-benefit-row h5 { font-size: 14px; font-weight: 600; margin: 0 0 4px; color: #222; }
        .syc-benefit-row p { font-size: 13px; color: #888; margin: 0; line-height: 1.6; }

        .syc-steps { margin-top: 8px; padding-top: 28px; border-top: 1px solid #e2e4f5; }
        .syc-steps-title {
          font-size: 13px; font-weight: 700; margin-bottom: 20px; color: #444;
          text-transform: uppercase; letter-spacing: 0.6px;
        }
        .syc-step-row { display: flex; gap: 14px; position: relative; padding-bottom: 24px; }
        .syc-step-row:last-child { padding-bottom: 0; }
        .syc-step-row:not(:last-child)::before {
          content: ''; position: absolute; left: 15px; top: 32px; bottom: 0; width: 1px; background: #d8dcf5;
        }
        .syc-step-num {
          width: 32px; height: 32px; border-radius: 50%; background: #717fe0; color: #fff;
          display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px;
          flex-shrink: 0; z-index: 1;
        }
        .syc-step-row h6 { font-size: 13px; font-weight: 600; margin: 0 0 3px; color: #222; }
        .syc-step-row p { font-size: 12px; color: #888; margin: 0; line-height: 1.5; }

        .syc-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

        .syc-submit-btn:hover:not(:disabled) {
          background: #717fe0 !important;
          border-color: #717fe0 !important;
          color: #fff !important;
        }
        .syc-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        @media (max-width: 991px) {
          .syc-layout { grid-template-columns: 1fr; }
        }
        @media (max-width: 560px) {
          .syc-info-card, .syc-form-card { padding: 28px 20px; }
          .syc-form-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 3,
  fontSize: 16, fontFamily: 'inherit',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  paddingRight: 32,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23666' stroke-width='1.5'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};

const submitStyle: React.CSSProperties = {
  padding: '13px 32px', background: '#222', color: '#fff', border: '1px solid #222',
  borderRadius: 23, fontSize: 15, cursor: 'pointer', transition: 'all 0.4s',
};

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, color: '#999' }}>{label}</div>
    <div style={{ color: '#222' }}>{value}</div>
  </div>
);

export default SellYourCar;
